import React, { useState, useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { QRCodeCanvas } from '@cheprasov/qrcode';
import FileSaver from 'file-saver';
import { Button, Input, Text, COLOURS } from '@shootsta/common-react';
import type { VideoShareSettings } from '../hooks/useVideoShareSettings';
import { getShareUrl } from '../utils/getShareUrl';

type PublicLinkSectionProps = {
  settings: VideoShareSettings | null;
  shareUrl?: string;
};

async function downloadQRCode(url: string) {
  const qrCanvas = new QRCodeCanvas(url, {
    fgColor: COLOURS.grey.denim
  });
  const dataUrlWithQRCode = await qrCanvas.toDataUrl();
  FileSaver.saveAs(dataUrlWithQRCode, 'qr-code.png');
}

function PublicLinkSection({ settings, shareUrl }: PublicLinkSectionProps) {
  const [copied, setCopied] = useState(false);

  const baseUrl = shareUrl || window.location.href;
  const publicUrl = getShareUrl(baseUrl);

  const onCopySuccess = useCallback(() => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, []);

  const handleDownloadQR = useCallback(() => {
    downloadQRCode(publicUrl);
  }, [publicUrl]);

  if (settings?.shareStatus !== 'PUBLIC') {
    return null;
  }

  return (
    <div className="video-share-modal__link-section">
      <Text noMargin highlighted>
        Public Link
      </Text>
      <Text noMargin placeholder className="video-share-modal__link-section__subtitle">
        Anyone with this link can view
      </Text>
      <Input
        defaultValue={publicUrl}
        key={publicUrl}
        className="video-share-modal__link-section__url"
        disabled
      />
      <div className="video-share-modal__link-section__actions">
        <CopyToClipboard text={publicUrl} onCopy={onCopySuccess}>
          <Button
            type="TERTIARY"
            prefix={copied ? 'success' : 'duplicate'}
            tooltipId="copy-public-link-btn"
            tooltipLabel={copied ? 'Copied!' : 'Copy link'}
          >
            Copy Link
          </Button>
        </CopyToClipboard>
        <Button
          type="TERTIARY"
          prefix="qrcode"
          tooltipId="qr-code-btn"
          tooltipLabel="Download QR code"
          onClick={handleDownloadQR}
        >
          QR Code
        </Button>
      </div>
    </div>
  );
}

export default PublicLinkSection;
